import * as Sentry from "@sentry/vue";

import { FolderSort } from "~/shared/enums";
import type { Comparator, Doc, EntityType } from "~/shared/types";
import { makeDocComparator, makeStringComparator } from "~/utils/comparator";

import type { PiniaActionAdaptor, PiniaGetterAdaptor } from "../shared";
import type { DataStore } from ".";

export type State = {
  _entityDuidsBlacklist: Set<string>;
  _entityDuidsPendingDeletion: Set<string>;
};

export const state: State = {
  _entityDuidsBlacklist: new Set(),
  _entityDuidsPendingDeletion: new Set(),
};

export type Getters = {
  $getDocSortingFn: (order: FolderSort) => Comparator<Doc | undefined>;
  $isEntityDuidBlacklisted: (duid: string) => boolean;
  $isEntityDuidPendingDeletion: (duid: string) => boolean;
};

export type Actions = {
  $blacklistEntityDuid: (entity: EntityType, duid: string) => void;
  $unblacklistEntityDuid: (duid: string) => void;
  $addEntityDuidPendingDeletion: (duid: string) => void;
  $removeEntityDuidPendingDeletion: (duid: string) => void;
};

const getters: PiniaGetterAdaptor<Getters, DataStore> = {
  $getDocSortingFn() {
    return (order) => {
      const orderToSortFn: Record<FolderSort, Comparator> = {
        [FolderSort.RECENT_ASC]: makeStringComparator((e: Doc) => e.updatedAt, false),
        [FolderSort.RECENT_DESC]: makeStringComparator((e: Doc) => e.updatedAt, true),
        [FolderSort.CREATED_ASC]: makeStringComparator((e: Doc) => e.createdAt, false),
        [FolderSort.CREATED_DESC]: makeStringComparator((e: Doc) => e.createdAt, true),
        [FolderSort.TITLE_ASC]: makeStringComparator((e: Doc) => e.title.toLowerCase(), false),
        [FolderSort.TITLE_DESC]: makeStringComparator((e: Doc) => e.title.toLowerCase(), true),
        [FolderSort.ORDER]: makeDocComparator(this.getSpaceByDuid, this.getFolderByDuid),
      };
      return orderToSortFn[order] || orderToSortFn[FolderSort.ORDER];
    };
  },
  $isEntityDuidBlacklisted() {
    return (duid: string) => this._entityDuidsBlacklist.has(duid);
  },
  $isEntityDuidPendingDeletion() {
    return (duid: string) => this._entityDuidsPendingDeletion.has(duid);
  },
};

const actions: PiniaActionAdaptor<Actions, DataStore> = {
  $blacklistEntityDuid(entity, duid) {
    this._entityDuidsBlacklist.add(duid);
    Sentry.captureMessage(`Blacklisted ${entity} with duid ${duid}`);
  },
  $unblacklistEntityDuid(duid) {
    this._entityDuidsBlacklist.delete(duid);
  },
  $addEntityDuidPendingDeletion(duid) {
    this._entityDuidsPendingDeletion.add(duid);
  },
  $removeEntityDuidPendingDeletion(duid) {
    this._entityDuidsPendingDeletion.delete(duid);
  },
};

export { actions, getters };
